import React, { useCallback, useState } from 'react';

import { useSelector } from 'react-redux';

import { ErrorMessage } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/glyph/add';
import { Box, Flex, Inline, xcss } from '@atlaskit/primitives';
import {
  AsyncCreatableSelect as AsyncCreatable,
  OptionType,
  OptionsType,
} from '@atlaskit/select';

import { ScreenReadersOnly } from 'src/components/accessibility';
import { PullRequestLabel } from 'src/components/types';
import { getCurrentPullRequestUrlPieces } from 'src/redux/pull-request/selectors';

import {
  sendCreatePullRequestLabelRequest,
  sendGetWorkspacePullRequestLabelsRequest,
  sendUpdatePullRequestLabelRequest,
} from './labels-utils';

interface AddLabelProps {
  prLabels: PullRequestLabel[];
  setPrLabels: (labels: PullRequestLabel[]) => void;
}

/* eslint @typescript-eslint/ban-types: "warn" */
export const AddLabel: React.FC<AddLabelProps> = ({
  prLabels,
  setPrLabels,
}) => {
  const { owner, slug, id } = useSelector(getCurrentPullRequestUrlPieces);
  const [createLabelIsError, setCreateLabelsIsError] = useState(false);
  const [createLabelErrorMessage, setCreateLabelErrorMessage] = useState('');
  const [createLabelIsLoading, setCreateLabelsIsLoading] = useState(false);
  const [
    getWorkspacePullRequestLabelsIsError,
    setGetWorkspacePullRequestLabelsIsError,
  ] = useState(false);
  const [isDuplicateLabelOnPr, setIsDuplicateLabelOnPr] = useState(false);
  const [duplicatePrLabel, setDuplicatePrLabel] = useState('');
  const [showCreateOption, setShowCreateOption] = useState(false);

  // logic to format the label text to be consistent.
  const formattedLabel = (rawLabel: string) => {
    return rawLabel.toLowerCase().replace(/ /g, '_');
  };

  // When we pick an existing label to add to the pr
  const handleUpdateLabel = useCallback(
    (labelContent: OptionType) => {
      if (labelContent) {
        setCreateLabelsIsLoading(true);
        sendUpdatePullRequestLabelRequest(
          owner,
          slug,
          labelContent.value as string,
          id
        ).then(({ isError, label, errorMessage }) => {
          setCreateLabelsIsError(isError);
          if (!isError && label) {
            setPrLabels([...prLabels, label]);
          } else if (isError) {
            setCreateLabelErrorMessage(errorMessage);
          }
        });
        setCreateLabelsIsLoading(false);
      }
    },
    [id, owner, setPrLabels, slug, prLabels]
  );

  // when we create a new label to add to the pr
  const handleAddLabel = useCallback(
    (labelContent: string) => {
      setCreateLabelsIsLoading(true);
      // Make sure label is not empty
      if (labelContent && labelContent !== '') {
        // send request
        sendCreatePullRequestLabelRequest(
          owner,
          slug,
          formattedLabel(labelContent),
          id
        ).then(({ isError, label, errorMessage }) => {
          setCreateLabelsIsError(isError);
          if (!isError && label) {
            setPrLabels([...prLabels, label]);
          } else if (isError) {
            setCreateLabelErrorMessage(errorMessage);
          }
        });
      }
      setCreateLabelsIsLoading(false);
    },
    [id, owner, setPrLabels, slug, prLabels]
  );

  const loadOptions = useCallback(
    (search: string, callback: (options: OptionsType) => void) => {
      sendGetWorkspacePullRequestLabelsRequest(owner)
        .then(({ isError, labels: workspaceLabels }) => {
          setGetWorkspacePullRequestLabelsIsError(isError);
          if (!isError && workspaceLabels) {
            setIsDuplicateLabelOnPr(false);

            // filter based on similar values
            let filteredLabels = workspaceLabels.filter(
              (prLabel: PullRequestLabel) =>
                prLabel.label.content
                  .toLowerCase()
                  .includes(formattedLabel(search))
            );

            // remove any labels that are already on the pr
            filteredLabels = filteredLabels.filter(
              (prLabel: PullRequestLabel) =>
                !prLabels.find(l => l.label.content === prLabel.label.content)
            );

            // check if want to show the create options item
            if (filteredLabels.length === 0) {
              setShowCreateOption(true);
            } else {
              setShowCreateOption(false);
            }

            // return the filtered labels
            callback(
              filteredLabels.map(
                (prLabel: PullRequestLabel) =>
                  ({
                    label: prLabel.label.content,
                    value: prLabel.label.content,
                  } as OptionType)
              )
            );
          } else {
            callback([]);
          }
        })
        .catch(_ => {
          setGetWorkspacePullRequestLabelsIsError(true);
        });
    },
    [prLabels, owner, setGetWorkspacePullRequestLabelsIsError]
  );

  const addLabelFormStyles = xcss({
    width: '100%',
  });

  // if there are no options, what message we show
  const selectMessage = () => {
    if (getWorkspacePullRequestLabelsIsError) {
      return <>Error retrieving labels</>;
    } else if (isDuplicateLabelOnPr) {
      return <>{duplicatePrLabel} was already added to the pull request</>;
    } else {
      return <>No options</>;
    }
  };

  // if the label is a duplicate, dont show the create option,
  // only show the create option if we have no other matching values
  const checkIsDuplicatePrLabel = (inputValue: string) => {
    const formattedInputValue = formattedLabel(inputValue);
    if (prLabels.some(l => l.label.content === formattedInputValue)) {
      setDuplicatePrLabel(formattedInputValue);
      setIsDuplicateLabelOnPr(true);
      return false;
    }
    setIsDuplicateLabelOnPr(false);
    return inputValue && showCreateOption;
  };

  return (
    <Box>
      <Flex>
        <Inline alignBlock="center">
          <Inline alignInline="center">
            <AddIcon label="Add Label" size="small" />
          </Inline>
        </Inline>
        <ScreenReadersOnly>Add Label</ScreenReadersOnly>
        <Box xcss={addLabelFormStyles}>
          <AsyncCreatable
            appearance="subtle"
            aria-label="addLabel"
            backspaceRemovesValue
            defaultOptions
            escapeClearsValue
            isClearable
            isLoading={createLabelIsLoading}
            isInvalid={createLabelIsError}
            isValidNewOption={checkIsDuplicatePrLabel}
            key={prLabels.length}
            name="addLabel"
            noOptionsMessage={selectMessage}
            placeholder="Add a label"
            loadOptions={loadOptions}
            onChange={handleUpdateLabel}
            onCreateOption={handleAddLabel}
          />
        </Box>
      </Flex>
      {createLabelIsError && (
        <ErrorMessage>
          <Box aria-live="polite">{createLabelErrorMessage}</Box>
        </ErrorMessage>
      )}
    </Box>
  );
};
